/**
 * Product Form Constants - Story 6.3: Create Add Product Form
 * Configuration constants for the multi-step product creation form
 */

import { ProductCategory } from '../types/productForm.types';

// ============================================================================
// Product Categories
// ============================================================================

/**
 * Product Categories Configuration
 * Maps category enum values to display labels
 */
export const PRODUCT_CATEGORIES: Array<{
  value: ProductCategory;
  label: string;
  description?: string;
}> = [
  {
    value: ProductCategory.GAUSHALA_PRODUCTS,
    label: 'Gaushala Products',
    description: 'Products from cattle including milk, ghee, etc.',
  },
  {
    value: ProductCategory.CATTLE,
    label: 'Cattle',
    description: 'Cows, bulls, and other livestock',
  },
  {
    value: ProductCategory.EQUIPMENT,
    label: 'Equipment',
    description: 'Farming and dairy equipment',
  },
  {
    value: ProductCategory.FEED,
    label: 'Feed',
    description: 'Animal feed and fodder',
  },
  {
    value: ProductCategory.SERVICES,
    label: 'Services',
    description: 'Veterinary, transportation, and other services',
  },
  {
    value: ProductCategory.OTHER,
    label: 'Other',
    description: 'Other products and services',
  },
];

// ============================================================================
// Product Units
// ============================================================================

/**
 * Product Units
 * Available units of measurement for product pricing
 */
export const PRODUCT_UNITS: string[] = [
  'kg',
  'liter',
  'piece',
  'dozen',
  'quintal',
  'bag',
  'bottle',
  'box',
  'carton',
  'packet',
  'bundle',
];

/**
 * Product Units with Labels
 * Units with display-friendly labels
 */
export const PRODUCT_UNITS_WITH_LABELS: Array<{
  value: string;
  label: string;
}> = [
  { value: 'kg', label: 'Kilogram (kg)' },
  { value: 'liter', label: 'Liter (L)' },
  { value: 'piece', label: 'Piece' },
  { value: 'dozen', label: 'Dozen (12 pcs)' },
  { value: 'quintal', label: 'Quintal (100 kg)' },
  { value: 'bag', label: 'Bag' },
  { value: 'bottle', label: 'Bottle' },
  { value: 'box', label: 'Box' },
  { value: 'carton', label: 'Carton' },
  { value: 'packet', label: 'Packet' },
  { value: 'bundle', label: 'Bundle' },
];

// ============================================================================
// Indian States and Districts
// ============================================================================

/**
 * Indian States
 * Complete list of Indian states and union territories
 */
export const INDIAN_STATES: string[] = [
  'Andhra Pradesh',
  'Arunachal Pradesh',
  'Assam',
  'Bihar',
  'Chhattisgarh',
  'Goa',
  'Gujarat',
  'Haryana',
  'Himachal Pradesh',
  'Jharkhand',
  'Karnataka',
  'Kerala',
  'Madhya Pradesh',
  'Maharashtra',
  'Manipur',
  'Meghalaya',
  'Mizoram',
  'Nagaland',
  'Odisha',
  'Punjab',
  'Rajasthan',
  'Sikkim',
  'Tamil Nadu',
  'Telangana',
  'Tripura',
  'Uttar Pradesh',
  'Uttarakhand',
  'West Bengal',
  // Union Territories
  'Andaman and Nicobar Islands',
  'Chandigarh',
  'Dadra and Nagar Haveli and Daman and Diu',
  'Delhi',
  'Jammu and Kashmir',
  'Ladakh',
  'Lakshadweep',
  'Puducherry',
];

/**
 * Districts by State
 * Mapping of states to their districts
 * Note: This is a representative list. In production, this should be
 * fetched from an API or a complete data source.
 */
export const DISTRICTS_BY_STATE: Record<string, string[]> = {
  'Andhra Pradesh': [
    'Anantapur',
    'Chittoor',
    'East Godavari',
    'Guntur',
    'Krishna',
    'Kurnool',
    'Nellore',
    'Prakasam',
    'Srikakulam',
    'Visakhapatnam',
    'Vizianagaram',
    'West Godavari',
    'YSR Kadapa',
  ],
  'Bihar': [
    'Araria',
    'Arwal',
    'Aurangabad',
    'Banka',
    'Begusarai',
    'Bhagalpur',
    'Bhojpur',
    'Buxar',
    'Darbhanga',
    'East Champaran',
    'Gaya',
    'Gopalganj',
    'Jamui',
    'Jehanabad',
    'Kaimur',
    'Katihar',
    'Khagaria',
    'Kishanganj',
    'Lakhisarai',
    'Madhepura',
    'Madhubani',
    'Munger',
    'Muzaffarpur',
    'Nalanda',
    'Nawada',
    'Patna',
    'Purnia',
    'Rohtas',
    'Saharsa',
    'Samastipur',
    'Saran',
    'Sheikhpura',
    'Sheohar',
    'Sitamarhi',
    'Siwan',
    'Supaul',
    'Vaishali',
    'West Champaran',
  ],
  'Gujarat': [
    'Ahmedabad',
    'Amreli',
    'Anand',
    'Aravalli',
    'Banaskantha',
    'Bharuch',
    'Bhavnagar',
    'Botad',
    'Chhota Udaipur',
    'Dahod',
    'Dang',
    'Devbhoomi Dwarka',
    'Gandhinagar',
    'Gir Somnath',
    'Jamnagar',
    'Junagadh',
    'Kheda',
    'Kutch',
    'Mahisagar',
    'Mehsana',
    'Morbi',
    'Narmada',
    'Navsari',
    'Panchmahal',
    'Patan',
    'Porbandar',
    'Rajkot',
    'Sabarkantha',
    'Surat',
    'Surendranagar',
    'Tapi',
    'Vadodara',
    'Valsad',
  ],
  'Karnataka': [
    'Bagalkot',
    'Ballari',
    'Belagavi',
    'Bengaluru Rural',
    'Bengaluru Urban',
    'Bidar',
    'Chamarajanagar',
    'Chikkaballapur',
    'Chikkamagaluru',
    'Chitradurga',
    'Dakshina Kannada',
    'Davanagere',
    'Dharwad',
    'Gadag',
    'Hassan',
    'Haveri',
    'Kalaburagi',
    'Kodagu',
    'Kolar',
    'Koppal',
    'Mandya',
    'Mysuru',
    'Raichur',
    'Ramanagara',
    'Shivamogga',
    'Tumakuru',
    'Udupi',
    'Uttara Kannada',
    'Vijayapura',
    'Yadgir',
  ],
  'Madhya Pradesh': [
    'Agar Malwa',
    'Alirajpur',
    'Anuppur',
    'Ashoknagar',
    'Balaghat',
    'Barwani',
    'Betul',
    'Bhind',
    'Bhopal',
    'Burhanpur',
    'Chhatarpur',
    'Chhindwara',
    'Damoh',
    'Datia',
    'Dewas',
    'Dhar',
    'Dindori',
    'Guna',
    'Gwalior',
    'Harda',
    'Hoshangabad',
    'Indore',
    'Jabalpur',
    'Jhabua',
    'Katni',
    'Khandwa',
    'Khargone',
    'Mandla',
    'Mandsaur',
    'Morena',
    'Narsinghpur',
    'Neemuch',
    'Panna',
    'Raisen',
    'Rajgarh',
    'Ratlam',
    'Rewa',
    'Sagar',
    'Satna',
    'Sehore',
    'Seoni',
    'Shahdol',
    'Shajapur',
    'Sheopur',
    'Shivpuri',
    'Sidhi',
    'Singrauli',
    'Tikamgarh',
    'Ujjain',
    'Umaria',
    'Vidisha',
  ],
  'Maharashtra': [
    'Ahmednagar',
    'Akola',
    'Amravati',
    'Aurangabad',
    'Beed',
    'Bhandara',
    'Buldhana',
    'Chandrapur',
    'Dhule',
    'Gadchiroli',
    'Gondia',
    'Hingoli',
    'Jalgaon',
    'Jalna',
    'Kolhapur',
    'Latur',
    'Mumbai City',
    'Mumbai Suburban',
    'Nagpur',
    'Nanded',
    'Nandurbar',
    'Nashik',
    'Osmanabad',
    'Palghar',
    'Parbhani',
    'Pune',
    'Raigad',
    'Ratnagiri',
    'Sangli',
    'Satara',
    'Sindhudurg',
    'Solapur',
    'Thane',
    'Wardha',
    'Washim',
    'Yavatmal',
  ],
  'Rajasthan': [
    'Ajmer',
    'Alwar',
    'Banswara',
    'Baran',
    'Barmer',
    'Bharatpur',
    'Bhilwara',
    'Bikaner',
    'Bundi',
    'Chittorgarh',
    'Churu',
    'Dausa',
    'Dholpur',
    'Dungarpur',
    'Hanumangarh',
    'Jaipur',
    'Jaisalmer',
    'Jalore',
    'Jhalawar',
    'Jhunjhunu',
    'Jodhpur',
    'Karauli',
    'Kota',
    'Nagaur',
    'Pali',
    'Pratapgarh',
    'Rajsamand',
    'Sawai Madhopur',
    'Sikar',
    'Sirohi',
    'Sri Ganganagar',
    'Tonk',
    'Udaipur',
  ],
  'Tamil Nadu': [
    'Ariyalur',
    'Chengalpattu',
    'Chennai',
    'Coimbatore',
    'Cuddalore',
    'Dharmapuri',
    'Dindigul',
    'Erode',
    'Kallakurichi',
    'Kanchipuram',
    'Kanyakumari',
    'Karur',
    'Krishnagiri',
    'Madurai',
    'Nagapattinam',
    'Namakkal',
    'Nilgiris',
    'Perambalur',
    'Pudukkottai',
    'Ramanathapuram',
    'Ranipet',
    'Salem',
    'Sivaganga',
    'Tenkasi',
    'Thanjavur',
    'Theni',
    'Thoothukudi',
    'Tiruchirappalli',
    'Tirunelveli',
    'Tirupathur',
    'Tiruppur',
    'Tiruvallur',
    'Tiruvannamalai',
    'Tiruvarur',
    'Vellore',
    'Viluppuram',
    'Virudhunagar',
  ],
  'Telangana': [
    'Adilabad',
    'Bhadradri Kothagudem',
    'Hyderabad',
    'Jagtial',
    'Jangaon',
    'Jayashankar Bhupalpally',
    'Jogulamba Gadwal',
    'Kamareddy',
    'Karimnagar',
    'Khammam',
    'Komaram Bheem',
    'Mahabubabad',
    'Mahabubnagar',
    'Mancherial',
    'Medak',
    'Medchal-Malkajgiri',
    'Mulugu',
    'Nagarkurnool',
    'Nalgonda',
    'Narayanpet',
    'Nirmal',
    'Nizamabad',
    'Peddapalli',
    'Rajanna Sircilla',
    'Rangareddy',
    'Sangareddy',
    'Siddipet',
    'Suryapet',
    'Vikarabad',
    'Wanaparthy',
    'Warangal Rural',
    'Warangal Urban',
    'Yadadri Bhuvanagiri',
  ],
  'Uttar Pradesh': [
    'Agra',
    'Aligarh',
    'Ambedkar Nagar',
    'Amethi',
    'Amroha',
    'Auraiya',
    'Ayodhya',
    'Azamgarh',
    'Baghpat',
    'Bahraich',
    'Ballia',
    'Balrampur',
    'Banda',
    'Barabanki',
    'Bareilly',
    'Basti',
    'Bhadohi',
    'Bijnor',
    'Budaun',
    'Bulandshahr',
    'Chandauli',
    'Chitrakoot',
    'Deoria',
    'Etah',
    'Etawah',
    'Farrukhabad',
    'Fatehpur',
    'Firozabad',
    'Gautam Buddha Nagar',
    'Ghaziabad',
    'Ghazipur',
    'Gonda',
    'Gorakhpur',
    'Hamirpur',
    'Hapur',
    'Hardoi',
    'Hathras',
    'Jalaun',
    'Jaunpur',
    'Jhansi',
    'Kannauj',
    'Kanpur Dehat',
    'Kanpur Nagar',
    'Kasganj',
    'Kaushambi',
    'Kushinagar',
    'Lakhimpur Kheri',
    'Lalitpur',
    'Lucknow',
    'Maharajganj',
    'Mahoba',
    'Mainpuri',
    'Mathura',
    'Mau',
    'Meerut',
    'Mirzapur',
    'Moradabad',
    'Muzaffarnagar',
    'Pilibhit',
    'Pratapgarh',
    'Prayagraj',
    'Rae Bareli',
    'Rampur',
    'Saharanpur',
    'Sambhal',
    'Sant Kabir Nagar',
    'Shahjahanpur',
    'Shamli',
    'Shrawasti',
    'Siddharthnagar',
    'Sitapur',
    'Sonbhadra',
    'Sultanpur',
    'Unnao',
    'Varanasi',
  ],
  'Delhi': [
    'Central Delhi',
    'East Delhi',
    'New Delhi',
    'North Delhi',
    'North East Delhi',
    'North West Delhi',
    'Shahdara',
    'South Delhi',
    'South East Delhi',
    'South West Delhi',
    'West Delhi',
  ],
};

// ============================================================================
// Form Configuration
// ============================================================================

/**
 * Form Configuration
 * Validation limits and constraints for the product form
 */
export const FORM_CONFIG = {
  /** Maximum number of images allowed per product */
  MAX_IMAGES: 5,
  /** Maximum file size in MB */
  MAX_FILE_SIZE_MB: 5,
  /** Maximum file size in bytes */
  MAX_FILE_SIZE_BYTES: 5 * 1024 * 1024,
  /** Allowed image MIME types */
  ALLOWED_IMAGE_TYPES: ['image/jpeg', 'image/png', 'image/webp'],
  /** Allowed image file extensions (for input accept attribute) */
  ALLOWED_IMAGE_EXTENSIONS: '.jpg,.jpeg,.png,.webp',
  /** Minimum product name length */
  MIN_PRODUCT_NAME_LENGTH: 3,
  /** Maximum product name length */
  MAX_PRODUCT_NAME_LENGTH: 100,
  /** Minimum description length */
  MIN_DESCRIPTION_LENGTH: 20,
  /** Maximum description length */
  MAX_DESCRIPTION_LENGTH: 2000,
  /** Maximum address length */
  MAX_ADDRESS_LENGTH: 500,
  /** Pincode length (exactly 6 digits for India) */
  PINCODE_LENGTH: 6,
  /** Maximum price value */
  MAX_PRICE: 99999999,
  /** Maximum quantity value */
  MAX_QUANTITY: 999999,
} as const;

// ============================================================================
// Form Steps Configuration
// ============================================================================

/**
 * Form Steps Configuration
 * Defines the steps in the multi-step product form wizard
 * Note: Simplified to 3 steps - Location and Review steps removed
 */
export const FORM_STEPS = [
  {
    step: 1,
    title: 'Product Details',
    description: 'Basic information about your product',
    icon: 'Package',
  },
  {
    step: 2,
    title: 'Pricing',
    description: 'Price, unit, and quantity',
    icon: 'IndianRupee',
  },
  {
    step: 3,
    title: 'Images',
    description: 'Upload product images',
    icon: 'ImageIcon',
  },
] as const;

/**
 * Total number of form steps
 */
export const TOTAL_FORM_STEPS = FORM_STEPS.length;

// ============================================================================
// Toast Messages
// ============================================================================

/**
 * Toast Messages
 * User-facing messages for various form actions and states
 */
export const TOAST_MESSAGES = {
  // Success messages
  SUCCESS_PRODUCT_CREATED: 'Product created successfully!',
  SUCCESS_IMAGE_UPLOADED: 'Image uploaded successfully.',
  SUCCESS_IMAGES_UPLOADED: 'All images uploaded successfully.',
  SUCCESS_DRAFT_SAVED: 'Draft saved successfully.',

  // Error messages
  ERROR_SUBMISSION_FAILED: 'Failed to create product. Please try again.',
  ERROR_IMAGE_UPLOAD_FAILED: 'Failed to upload images. Please try again.',
  ERROR_VALIDATION: 'Please check the form for errors and try again.',
  ERROR_FILE_SIZE: 'File size exceeds 5MB limit.',
  ERROR_FILE_TYPE: 'Only JPEG, PNG, and WebP images are allowed.',
  ERROR_MAX_IMAGES: 'Maximum 5 images allowed.',
  ERROR_MIN_IMAGES: 'At least 1 image is required.',
  ERROR_NETWORK: 'Network error. Please check your connection.',
  ERROR_UNAUTHORIZED: 'Session expired. Please log in again.',
  ERROR_SERVER: 'Server error. Please try again later.',

  // Warning messages
  WARNING_UNSAVED_CHANGES: 'You have unsaved changes. Are you sure you want to leave?',
  WARNING_IMAGE_REMOVED: 'Image removed.',
  WARNING_DRAFT_EXISTS: 'You have an unsaved draft. Would you like to continue?',

  // Info messages
  INFO_UPLOADING: 'Uploading images...',
  INFO_SUBMITTING: 'Creating product...',
  INFO_VALIDATING: 'Validating form...',
} as const;

// ============================================================================
// API Configuration
// ============================================================================

/**
 * API Endpoints
 * Endpoints for product-related API calls
 */
export const API_ENDPOINTS = {
  /** Create a new product */
  CREATE_PRODUCT: '/commerce-service/api/v1/seller/products',
  /** Upload product image */
  UPLOAD_IMAGE: '/commerce-service/api/v1/seller/products/images',
  /** Get seller's products */
  GET_PRODUCTS: '/commerce-service/api/v1/seller/products',
} as const;

// ============================================================================
// Default Values
// ============================================================================

/**
 * Default Form Values
 * Initial values for the product form
 */
export const DEFAULT_FORM_VALUES = {
  name: '',
  category: '' as ProductCategory | '',
  description: '',
  price: '' as number | '',
  unit: '',
  quantity: '' as number | '',
  images: [],
  state: '',
  district: '',
  pincode: '',
  address: '',
} as const;

/**
 * Default Product Category
 * Fallback category if none selected
 */
export const DEFAULT_CATEGORY = ProductCategory.OTHER;

// ============================================================================
// Accessibility
// ============================================================================

/**
 * Accessibility Labels
 * ARIA labels for form elements
 */
export const A11Y_LABELS = {
  FORM: 'Add new product form',
  STEP_INDICATOR: 'Form progress',
  PRODUCT_NAME: 'Product name',
  CATEGORY_SELECT: 'Product category',
  DESCRIPTION: 'Product description',
  PRICE_INPUT: 'Product price in rupees',
  UNIT_SELECT: 'Unit of measurement',
  QUANTITY_INPUT: 'Available quantity',
  IMAGE_UPLOAD: 'Upload product images',
  IMAGE_PREVIEW: 'Product image preview',
  REMOVE_IMAGE: 'Remove image',
  STATE_SELECT: 'Select state',
  DISTRICT_SELECT: 'Select district',
  PINCODE_INPUT: 'Enter 6-digit pincode',
  ADDRESS_INPUT: 'Enter detailed address',
  NEXT_BUTTON: 'Go to next step',
  PREVIOUS_BUTTON: 'Go to previous step',
  SUBMIT_BUTTON: 'Submit product',
  CANCEL_BUTTON: 'Cancel and go back',
} as const;
